import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { PartSearchHttpClient } from "../types";
import { createSearchParts } from "../utils/partSearchApi";

function useDebouncedValue<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export type UsePartSearchOptions = {
  axios: PartSearchHttpClient;
  searchUrl?: string;
  query: string;
  brand?: string;
  limit?: number;
  /** Minimum trimmed query length before search runs. Default 3. */
  minQueryLength?: number;
  enabled?: boolean;
  debounceMs?: number;
};

export function usePartSearch({
  axios,
  searchUrl,
  query,
  brand,
  limit = 15,
  minQueryLength = 3,
  enabled = true,
  debounceMs = 500,
}: UsePartSearchOptions) {
  const searchParts = useMemo(
    () => createSearchParts(axios, searchUrl),
    [axios, searchUrl],
  );
  const debouncedQuery = useDebouncedValue(query, debounceMs);
  const trimmed = debouncedQuery.trim();
  const canSearch = enabled && trimmed.length >= minQueryLength;

  return useQuery({
    queryKey: ["part-search", trimmed, brand, limit, searchUrl],
    queryFn: () =>
      searchParts({
        q: trimmed,
        limit,
        brand: brand?.trim() || undefined,
      }),
    enabled: canSearch,
  });
}
